import React, { Fragment, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@progress/kendo-react-buttons";
import {
  setFormValue,
  setCurrentStepIndex,
  setSteps,
  setProcessing,
  importScheduleReport,
} from "../../../redux/slices/importschedulepageslice";
import { Form, FormElement } from "@progress/kendo-react-form";
import { formValidator } from "./importschedulepageformvalidator";
import { Stepper } from "@progress/kendo-react-layout";
import { addNotification } from "../../../redux/slices/notificationsslice";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiErrorsHandler, getErrorMessages } from "../../../api/httpclient";
import { notificationDurationMiddle } from "../../../lib/utils";
import { setCurrentPageIsDirty } from "../../../redux/slices/currentpageslice";

export const ImportSchedulePageForm = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentTheme = useSelector((state) => state.currentTheme);
  const appearance = useSelector((state) => state.appearance);
  const pageState = useSelector((state) => state.importSchedulePage);
  const updateDirtyState = props.updateDirtyState;
  const currentStepIndex = pageState.currentStepIndex;
  const { t } = useTranslation();

  const steps = pageState.steps;
  const stepPages = pageState.stepPages;
  const stepperOrientation = pageState.stepperOrientation;

  const lastStepIndex = steps.length - 1;
  const isLastStep = lastStepIndex === currentStepIndex;
  const isPreviousStepsValid =
    steps
      .slice(0, currentStepIndex)
      .findIndex((currentStep) => currentStep.isValid === false) === -1;

  //handle is dirty
  useEffect(() => {
    updateDirtyState();
  }, [pageState.formInitialValues, pageState.formValues]);

  const validateDataset = async (values) => {
    if (!values.datasetId) {
      dispatch(
        addNotification({
          text: t("schedule.selectdatasetmsg"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        }),
      );
      return false;
    }
    return true;
  };

  const validateGeneralForm = async (values) => {
    const name = values.Name;
    const type = values.Type;
    const source = values.Source;
    const file = values.File;
    const sheetName = values.SheetName;

    if (!name || !type || !source) {
      dispatch(
        addNotification({
          text: t("schedule.fillmandatoryfields"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        }),
      );
      return false;
    }

    if (source == 1 || source == "1") {
      if (!file || !sheetName) {
        dispatch(
          addNotification({
            text: t("schedule.fillmandatoryfields"),
            type: "error",
            closable: true,
            duration: 3,
            position: "topRight",
          }),
        );
        return false;
      }
    }
    return true;
  };

  const validateRecurrency = async (values) => {
    if (!values.RecurrencyFrom) {
      dispatch(
        addNotification({
          text: t("schedule.selectrecur"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        }),
      );
      return false;
    }
    if (values.RecurrencyFrom == "fromRecurrency" && !values.RecurrsEvery) {
      dispatch(
        addNotification({
          text: t("schedule.selectrecurrency"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        }),
      );
      return false;
    }

    if (values.RecurrencyFrom == "fromCron" && !values.CronValue) {
      dispatch(
        addNotification({
          text: t("schedule.selectcronvalue"),
          type: "error",
          closable: true,
          duration: 3,
          position: "topRight",
        }),
      );
      return false;
    }
    return true;
  };

  const validateReview = async (values) => {
    dispatch(importScheduleReport(values, t)).then(
      (response) => {
        dispatch(setCurrentPageIsDirty(false));
        dispatch(
          addNotification({
            text: t("schedule.createsucceeded"),
            type: "success",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          }),
        );
      },
      (err) => {
        //hide loader
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("datacorrection.unknown_error")];
          else errorMessages = getErrorMessages(errorMessages, 1);

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("datacorrection.unknown_error");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
    return true;
  };

  const checkStepValidity = async (values) => {
    debugger;
    let stepIsValid = true;
    if (currentStepIndex === 0) {
      stepIsValid = await validateDataset(values);
    } else if (currentStepIndex === 1) {
      stepIsValid = await validateGeneralForm(values);
    } else if (currentStepIndex === 2) {
      stepIsValid = await validateRecurrency(values);
    } else if (currentStepIndex === 3) {
      stepIsValid = await validateReview(values);
    }
    return stepIsValid;
  };

  const onStepSubmit = React.useCallback(
    async (event) => { 
      const values = event.values;
      // Validate the current step
      const stepIsValid = await checkStepValidity(values);

      // Update steps validity
      const updatedSteps = steps.map((step, index) => ({
        ...step,
        isValid: index === currentStepIndex ? stepIsValid : step.isValid,
      }));
      dispatch(setSteps(updatedSteps));

      if (stepIsValid) {
        dispatch(setFormValue(values)); // Persist updated form values to Redux

        if (currentStepIndex < lastStepIndex) {
          dispatch(setCurrentStepIndex(currentStepIndex + 1));
        } else if (isLastStep) {
          // Replace with actual submission logic
          alert(JSON.stringify(values));
        }
      }
    },
    [steps, currentStepIndex, lastStepIndex, isLastStep],
  );

  const onPrevClick = React.useCallback(
    (event) => {
      event.preventDefault();
      dispatch(setCurrentStepIndex(Math.max(currentStepIndex - 1, 0)));
    },
    [currentStepIndex, dispatch],
  );

  return (
    <Fragment>
      <div className="form-wrapper">
        <div className={"stepper-wrapper"}>
          <Stepper
            value={currentStepIndex}
            items={steps}
            orientation={stepperOrientation}
          />
        </div>
        <Form
          className="create-schedule-page-form"
          validator={formValidator}
          key={pageState.formKey}
          onSubmitClick={onStepSubmit}
          initialValues={pageState.formValues}
          render={(formRenderProps) => {
            const StepComponent = stepPages[currentStepIndex];
            return (
              <FormElement horizontal={false}>
                <StepComponent
                  formValues={pageState.formValues}
                  dispatch={dispatch}
                  onChange={formRenderProps.onChange}
                  onChangeDataset={props.onChangeDataset}
                  onChangeFormValue={props.onChangeFormValue}
                  onRecurrencyOptionChange={props.onRecurrencyOptionChange}
                  loadFilterDataFromDataset={props.loadFilterDataFromDataset}
                  loadGoogleConnectorSheets={props.loadGoogleConnectorSheets}
                  loadGoogleConnections={props.loadGoogleConnections}
                  getGoogleSheetId={props.getGoogleSheetId}
                />
                {/* Navigation Buttons */}
                <div className="navigation-buttons">
                  <div>
                    {!isLastStep && (
                      <Button
                        className="btn-proceed"
                        onClick={formRenderProps.onSubmit}
                        type="submit"
                      >
                        {currentStepIndex == 3
                          ? t("datasets.create.createbtn")
                          : t("datasets.create.nextbtn")}
                      </Button>
                    )}

                    {!isLastStep && currentStepIndex !== 0 && (
                      <Button className="btn-cancel" onClick={onPrevClick}>
                        {t("datasets.create.previousbtn")}
                      </Button>
                    )}
                  </div>
                </div>
              </FormElement>
            );
          }}
        />
      </div>
      <style>
        {`        
                    .create-schedule-page-form .form-wrapper{
                        width:100%;
                    }
                    .create-schedule-page-form .form-wrapper .stepper-wrapper{
                      padding:20px;
                        width:100%;
                        border: ${currentTheme.managerBorder};
                        background-color: ${currentTheme.componentBgFaded};
                        border-left:0;
                    }
                    .create-schedule-page-form .form-wrapper .k-stepper{
                        direction: ${appearance.isRightToLeft ? "rtl" : "ltr"}
                    }
                   .create-schedule-page-form .form-wrapper .k-stepper .k-step-list {
                      display: flex;
                      justify-content: space-between; /* Pushes first and last steps to edges */
                      width: 100%;
                    }

                    .create-schedule-page-form .form-wrapper .k-stepper .k-step-list .k-step {
                      flex: 1; /* Ensures middle steps spread out */
                      text-align: center;
                    }

                    // .create-schedule-page-form .form-wrapper .k-stepper .k-step-list .k-step.k-step-first {
                    //   text-align: left; /* Aligns first step to left */
                    // }

                    // .create-schedule-page-form .form-wrapper .k-stepper .k-step-list .k-step.k-step-last {
                    //   text-align: right; /* Aligns last step to right */
                    // }

                    .create-schedule-page-form .form-wrapper .k-stepper .k-step-list .k-step.k-step-first {
                      justify-content: flex-start;
                      text-align: left;
                      margin-right: auto;
                    }

                    .create-schedule-page-form .form-wrapper .k-stepper .k-step-list .k-step.k-step-last {
                      justify-content: flex-end;
                      text-align: right;
                      margin-left: auto;
                    }

                    .create-schedule-page-form .k-form
                    {          
                        /*the kendo form which is child of my form.content*/     
                        width: 100%;
                        display: flex;
                        flex: 1;
                        justify-content: space-between;
                        flex-direction:column;
                       
                        border: ${currentTheme.managerBorder};
                        padding:15px;
                        overflow-x:auto;

                    }

                    .create-schedule-page-form .form-region-1
                    {
                        width: 100%;
                        max-width: 1000px;
                        margin-top: 20px !important;/*to override kendo first child of k-form having marginTop=0*/                     
                    }

                    .create-schedule-page-form .form-region-1 .form-group-1
                    {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                    }

                   
                    .create-schedule-page-form > .content
                    {
                        padding: 0px;
                        background-color: transparent;
                        align-items: flex-start;
                    }

                    .create-schedule-page-form .form-region-root
                    {
                        background-color: transparent;
                    }
                    
                   
                    .create-schedule-page-form .form-group-1 .toggle-container
                    {
                        /*the div containing the 3 checks*/
                        justify-content: space-between;
                        border-color: ${currentTheme.componentBorderColor};
                        width: 100%;/*620px;*/
                        max-width: 1000px;
                    }

                    .create-schedule-page-form input.k-input, .create-schedule-page-form textarea.k-textarea, 
                    .create-schedule-page-form .k-dropdownlist .k-input-inner
                    {
                        /*make input controls have a bit more height*/
                        padding: 6px 8px;
                    } 

                    .snippet-popup .k-menu-item:nth-child(2)
                    {
                        padding: 0 !important;
                    }

                    .snippet-popup .k-menu-item:nth-child(1),
                    .snippet-popup .k-menu-item:nth-child(3)
                    {
                        padding-top: 3px;
                        padding-bottom: 3px;
                    }


                    .create-schedule-page-form .k-form-field textarea
                    {
                        height: 200px !important;
                    }

                    .create-schedule-page-form .navigation-buttons{
                      display: flex;
                      justify-content: space-between;
                      align-content: center;
                      direction: rtl;
                      margin-top:50px;
                    }
                    .create-schedule-page-form .navigation-buttons .btn-proceed{
                      padding: 6px 28px;
                      border-radius: 0px;
                      border-color: ${currentTheme.primary};
                      color: #fff;
                      min-width:120px;
                      background-color:  ${currentTheme.primary};
                    }
                    .create-schedule-page-form .navigation-buttons .btn-cancel{
                      margin-right: 16px;
                      padding: 6px 28px;
                      min-width:120px;
                      border-radius: 0px;
                      border-color: rgba(0, 0, 0, 0.1);
                      color: #424242;
                      background-color: #f5f5f5;
                    }


                    }
                `}
      </style>
    </Fragment>
  );
};
